import * as CL from '@design-system/component-library';
import { useContext, useRef } from 'react';
import { useNavigate } from 'react-router';
import { paths } from '../../common/constants/pathVariables';
import {
  closeMsg,
  englishMsg,
  forCorporationsMsg,
  forTheEmployeeMsg,
  logInMsg,
  logOutMsg,
  suomiMsg,
  t,
} from '../../common/i18n';
import { useAuth } from '../../public/site/AuthProvider';
import { useConfig } from '../../public/site/ConfigProvider';
import { SiteContext } from '../../public/site/SiteContext';
import { headerAccordions } from '../Header/dynamic/headerAccordions';
import { siteSelect } from '../Header/dynamic/headerFunctions';
import type { IndividualDetails } from '../../generated/api/individualDetails';

export interface PreAuthHeaderProps {
  isEoe: boolean;
  individualDetails?: IndividualDetails;
  isPublic?: boolean;
}

export const PreAuthHeader = ({ isEoe, individualDetails, isPublic }: PreAuthHeaderProps) => {
  const navigate = useNavigate();
  const { siteLanguage, saveUserPreferredLanguage, siteBaseUrl } = useContext(SiteContext);
  const { logout } = useAuth();
  const hamburgerRef = useRef<HTMLAnchorElement | HTMLButtonElement>(null);
  const preAuthNavigationId = 'pre-auth-oma-elisa-navigation';

  const languageSelect = {
    languages: [
      { name: suomiMsg, abbr: 'fi' },
      { name: englishMsg, abbr: 'en' },
    ],
    selectedLanguage: siteLanguage.substring(0, 2),
    onLanguageChange: ({ lang }: { lang: string }) => {
      saveUserPreferredLanguage(lang);
    },
  };

  const { ringBaseUrl } = useConfig();

  const user = {
    email: '',
    fullName: `${individualDetails?.firstName ?? ''} ${individualDetails?.lastName ?? ''}`.trim(),
    name: individualDetails?.firstName ?? '',
  };

  return (
    <CL.Header
      desktopBreakpointOffset={35}
      isLoggedIn={!isPublic}
      languageSelect={languageSelect}
      siteSelect={siteSelect(siteBaseUrl)}
    >
      <CL.HeaderNavigationSection>
        <CL.HeaderLogoAndService
          logo={{
            label: 'Elisa',
            onClick: e => {
              e.preventDefault();
              navigate(paths.SELECT_SIGNATORY_RIGHTS);
            },
            url: '/',
          }}
          service={{
            identifiers: ['OmaElisa', isEoe ? t.ACGC(forTheEmployeeMsg) : t.KBLX(forCorporationsMsg)],
            label: '',
            onClick: e => {
              e.preventDefault();
              navigate(paths.SELECT_SIGNATORY_RIGHTS);
            },
            url: paths.SELECT_SIGNATORY_RIGHTS,
          }}
        />
        <CL.HeaderNavigation
          id={preAuthNavigationId}
          navigationItemList={[]}
          languageSelect={languageSelect}
          returnFocusToRef={hamburgerRef}
          siteSelect={siteSelect(siteBaseUrl)}
        />
      </CL.HeaderNavigationSection>
      <CL.HeaderNavigationSection>
        {!isPublic && (
          <>
            <CL.HeaderServicesMenu
              i18nButtonLabel={t.A77Z('Navigate')}
              servicesMenuLinkAccordions={headerAccordions(navigate, ringBaseUrl)}
            />
            <CL.HeaderUserMenu
              i18nLogin={t.Z16I(logInMsg)}
              user={user}
              userPronounText={user.name}
              logout={{ onClick: () => logout('', false, true), title: t.LQ3X(logOutMsg) }}
            />
          </>
        )}
        <CL.HeaderHamburger
          i18nCloseAlt={t.WOYD(closeMsg)}
          i18nOpenAlt="menu"
          componentRef={hamburgerRef}
          toggledModalChild={preAuthNavigationId}
        />
      </CL.HeaderNavigationSection>
    </CL.Header>
  );
};
